/** Admin panel localStorage API token helpers. */

// API Token Management
const API_TOKEN_KEY = 'apiToken';

const getApiToken = (): string | null => {
  return localStorage.getItem(API_TOKEN_KEY);
};

const storeApiToken = (token: string): void => {
  localStorage.setItem(API_TOKEN_KEY, token);
};

// API Parameter Building
export type SortingState = { id: string; desc: boolean }[];
export type FilterState = { id: string; value: any }[];

export function buildApiParams({
  page,
  size,
  sorting,
  filters,
}: {
  page: number;
  size: number;
  sorting?: SortingState;
  filters?: FilterState;
}) {
  const params: Record<string, any> = {
    page,
    size,
  };

  if (sorting?.length) {
    params.order_by = sorting.map(({ id, desc }) => (desc ? `-${id}` : id)).join(',');
  }

  for (const { id, value } of filters ?? []) {
    if (value === null || value === '') continue;

    const baseId = id.replace(/__in$/, '');

    if (Array.isArray(value)) {
      params[`${baseId}__in`] = value.join(',');
    } else {
      params[baseId] = value;
    }
  }

  return params;
}

/**
 * Safely extract a human-readable error message from an Axios error response.
 * FastAPI's `detail` can be a plain string *or* an array of Pydantic
 * validation-error objects (`{type, loc, msg, input, ctx, url}`).
 * Passing the array straight into a React render tree (e.g. a toaster
 * description) crashes with "Objects are not valid as a React child".
 */
function formatDetailItem(item: unknown): string {
  if (typeof item === 'string') return item;
  if (item && typeof item === 'object') {
    const row = 'row' in item ? item.row : undefined;
    const errors = 'errors' in item ? item.errors : undefined;
    if (row !== undefined && errors) return `Row ${String(row)}: ${String(errors)}`;
    if ('loc' in item && 'msg' in item && Array.isArray(item.loc) && typeof item.msg === 'string') {
      return `${item.loc.join('.')}: ${item.msg}`;
    }
    if ('msg' in item && typeof item.msg === 'string') return item.msg;
  }
  return JSON.stringify(item);
}

/** All human-readable messages from a FastAPI/Axios error (CSV row errors, pydantic lists). */
export function getApiErrorMessages(error: unknown, fallback: string): string[] {
  const detail = (error as { response?: { data?: { detail?: unknown } } })?.response?.data?.detail;

  if (typeof detail === 'string') return [detail];
  if (Array.isArray(detail) && detail.length > 0) return detail.map(formatDetailItem);
  return [fallback];
}

export function getApiErrorMessage(error: unknown, fallback: string): string {
  return getApiErrorMessages(error, fallback)[0] ?? fallback;
}

export { getApiToken, storeApiToken };
